// src/views/vendor/settings/VendorAvailability.js
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Typography, Switch, TextField, Button, FormControlLabel, Skeleton } from '@mui/material';
import vendorService from 'api/vendorService';
import { gridSpacing } from 'store/constant';
import { toast } from 'react-toastify';

const VendorAvailability = ({ data, isLoading }) => {
    const [isAvailable, setIsAvailable] = useState(false);
    const [openingHour, setOpeningHour] = useState('09:00');
    const [closingHour, setClosingHour] = useState('18:00');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (data?.vendor) {
            setIsAvailable(data.vendor.isAvailable || false);
            setOpeningHour(data.vendor.openingHour || '00:00');
            setClosingHour(data.vendor.closingHour || '00:00');
        }
    }, [data]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const payload = { isAvailable, openingHour, closingHour };
            await vendorService.updateVendor(payload);
            toast.success('Availability updated successfully!');
        } catch (error) {
            toast.error('Failed to update availability');
        } finally {
            setLoading(false);
        }
    };

    // 🦴 Show skeletons while loading vendor data
    if (isLoading) {
        return (
            <Grid container spacing={gridSpacing} paddingX={5} pt={3}>
                <Grid item md={5}>
                    <Skeleton variant="text" width={220} height={40} />
                    <Skeleton variant="text" width={320} height={20} />
                </Grid>

                <Grid item md={2}>
                    -
                </Grid>

                <Grid item md={5}>
                    <Skeleton variant="rectangular" height={40} width="40%" sx={{ mb: 2, borderRadius: 2 }} />
                    <Box display="flex" gap={2} mt={2}>
                        <Skeleton variant="rectangular" height={56} sx={{ flex: 1, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={56} sx={{ flex: 1, borderRadius: 2 }} />
                    </Box>
                    <Skeleton variant="rectangular" width={120} height={40} sx={{ mt: 3, borderRadius: 2 }} />
                </Grid>
            </Grid>
        );
    }

    // 🧾 Actual content
    return (
        <Grid container spacing={gridSpacing} paddingX={5} pt={3}>
            <Grid item md={5}>
                <Typography fontWeight="bold" fontSize={20}>
                    Business Availability
                </Typography>
                <Typography color="grey" fontSize={14}>
                    Set your business hours and availability status below.
                </Typography>
            </Grid>

            <Grid item md={2}>
                -
            </Grid>

            <Grid item md={5}>
                <FormControlLabel
                    control={<Switch checked={isAvailable} onChange={(e) => setIsAvailable(e.target.checked)} color="primary" />}
                    label={isAvailable ? 'Available' : 'Not Available'}
                />

                <Box display="flex" gap={2} mt={2}>
                    <TextField
                        type="time"
                        label="Opening Hour"
                        value={openingHour}
                        onChange={(e) => setOpeningHour(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        type="time"
                        label="Closing Hour"
                        value={closingHour}
                        onChange={(e) => setClosingHour(e.target.value)}
                        fullWidth
                    />
                </Box>

                <Button variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading} onClick={handleSave}>
                    {loading ? 'Saving...' : 'Save Changes'}
                </Button>
            </Grid>
        </Grid>
    );
};

VendorAvailability.propTypes = {
    data: PropTypes.shape({
        vendor: PropTypes.shape({
            isAvailable: PropTypes.bool,
            openingHour: PropTypes.string,
            closingHour: PropTypes.string
        })
    }),
    isLoading: PropTypes.bool
};

export default VendorAvailability;
